import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { SafeHtml } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { CountriesService } from 'src/app/core/services/countries.service';
import { FacilityObject } from 'src/app/core/services/facility.model';
import { FacilityService } from 'src/app/core/services/facility.service';
import { FeatureFlagsService } from 'src/app/core/shared-service/feature-flags.service';
import { environment } from 'src/environments/environment';
import { RootScopeDeclare } from '../../core/rootscope-declare';
import { AuthenticationService } from '../../core/services/authentication.service';
import { RootScopeService } from '../../core/shared-service/root-scope.service';
import { ToastMessageService } from '../../core/shared-service/toast-message.service';
import { CommonUtil } from '../../core/Utils/common-util';
import { DeprecatedFeatureType } from '../deprecated-banner/deprecated-banner.interface';
import { CheckSsoEnabledService } from '../services/check-sso-enabled.service';
import { ConsoleRightsService } from '../services/console-rights.service';
import { MultiFacilityService } from '../services/multi-facility.service';
import { PageTitleService } from '../services/page-title.service';
import { SubscriptionService } from '../services/subscription.service';
import { UserLogoutService } from '../services/user-logout.service';

@Component({
  selector: 'app-post-login-base',
  templateUrl: './post-login-base.component.html',
  styleUrls: ['./post-login-base.component.scss'],
})
export class PostLoginBaseComponent implements OnInit, OnDestroy {
  @HostListener('window:scroll', ['$event']) onScroll() {
    this.rootScopeService.scrolled$.next();
  }
  rootScopeData: RootScopeDeclare;
  logoutData: any;
  isSSOEnabled = false;
  indexPath: string;
  indexParams: any;
  ssoRedirectionParams: any;
  toastMessage: string | SafeHtml | null | undefined;
  showToast: boolean;
  private sub = new Subscription();
  @Input() isAuthSuccess: boolean;
  showAnnouncementModal = false;
  currentFacility: FacilityObject;
  currentRoute: string;
  isHaloLayoutLoad = true;
  upgradeURL: string;
  manageSubscriptionURL: string;
  currentUserID: number;
  showDeprecatedFeatureBanner = false;
  featureRoutes = [
    {
      route: '/equipmentSettings/healthSafety',
      feature: 'Health & Safety',
      trxContext: 'DEPRECATED.FEATURES.BANNER.DESCRIPTION.HEALTHSAFETY',
    },
    {
      route: '/equipmentSettings/workout',
      feature: 'Custom Workouts',
      trxContext: 'DEPRECATED.FEATURES.BANNER.DESCRIPTION.CUSTOMWORKOUT',
    },
    {
      route: '/facility/rfid',
      feature: 'RFID Members',
      trxContext: 'DEPRECATED.FEATURES.BANNER.DESCRIPTION.RFIDMEMBERS',
    },
    {
      route: '/facility/equipment/getWidget',
      feature: 'Trip Planner',
      trxContext: 'DEPRECATED.FEATURES.BANNER.DESCRIPTION.TRIPPLANNER',
    },
  ]; //Routes to show DeprecatedFeatureBanner
  deprecatedBannerTitle: string;
  deprecatedBannerDescription: string;
  deprecatedFeatureType = DeprecatedFeatureType.FeatureSupportUpdate;
  fcHybridLegacHalo = false;
  checkedFCHybridMode = false;
  constructor(
    private rootScopeService: RootScopeService,
    private consoleRightsService: ConsoleRightsService,
    private router: Router,
    private checkSsoEnabledService: CheckSsoEnabledService,
    private userLogoutService: UserLogoutService,
    private cookieService: CookieService,
    private toastMessageService: ToastMessageService,
    private authenticationService: AuthenticationService,
    private commonUtil: CommonUtil,
    private _facilityService: FacilityService,
    private multiFacService: MultiFacilityService,
    private _countriesService: CountriesService,
    private _subscriptionService: SubscriptionService,
    private _amplitudeAnalytics: AmplitudeAnalyticsService,
    private pageTitleService: PageTitleService,
    private loader: HaloLoaderService,
    private featureService: FeatureFlagsService
  ) {
    this.getRootScopeData();
    this.currentRoute = this.router.url;
    const routeSub = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = this.router.url;
        this.setDeprecatedFeatureBanner();
        this.commonUtil.scrollTop();
      }
    });

    this.sub.add(routeSub);

    const toastMessageSub = toastMessageService.changeEmitted$.subscribe((text) => {
      this.enableToast(text);
    });

    this.sub.add(toastMessageSub);
  }

  private setDeprecatedFeatureBanner(): void {
    if (!this.currentRoute) return;

    const deprecatedFeatureRoute = this.featureRoutes.find((featureRoute) =>
      this.currentRoute.includes(featureRoute.route)
    );

    if (deprecatedFeatureRoute) {
      this.showDeprecatedFeatureBanner = true;
      this.deprecatedBannerTitle = this.commonUtil.translateText('DEPRECATED.FEATURES.BANNER.TITLE');
      this.deprecatedBannerDescription = this.commonUtil.translateText(deprecatedFeatureRoute.trxContext);
    } else {
      this.showDeprecatedFeatureBanner = false;
    }
  }

  getRootScopeData(): void {
    this.rootScopeService.getRootScope().subscribe((rootScopeData) => {
      this.rootScopeData = rootScopeData;
      if (this.rootScopeData.announcementModals.length > 0) this.enableAnnouncementModal();
    });
  }

  async ngOnInit() {
    this._countriesService.get();
    this.getConsoleRightsData();
    await this.getFacilities();

    this.rootScopeData.unsavedChangesPopupData = {
      title: 'UNSAVED.CHANGES',
      message: 'UNSAVED.CHANGES.MESSAGE',
      isWarningPopup: true,
      ctaButtonLabel: 'YES.CONTINUE',
      negativeButtonLabel: 'NO',
      isSecondaryTextButton: false,
    };

    this.rootScopeData.customPopupData = {
      title: 'OOPS',
      message: 'CUSTOM.LOADER.ERROR.MSG.APIFAILED',
      isWarningPopup: true,
      ctaButtonLabel: 'OK',
      isSecondaryTextButton: false,
      hideSecondaryButton: true,
    };

    this.rootScopeData.popupBlockerMessageData = {
      title: 'POP.UP.BLOCKER.DETECTED',
      message: 'POP.UP.BLOCKER.MSG',
      isWarningPopup: true,
      ctaButtonLabel: 'OK',
      isSecondaryTextButton: false,
      hideSecondaryButton: true,
    };

    this.currentFacility = this._facilityService.selectedFacility();
    const facilityOnChangeSub = this._facilityService.selectedFacilitySource.subscribe((res) => {
      this.currentFacility = res;
    });
    this.sub.add(facilityOnChangeSub);

    this._amplitudeAnalytics.setUser(this.rootScopeData.currentUser.userId.toString());
    //Subscribe autoLogout from authentication service to singout by expiryTimeOut.
    this.authenticationService.autoLogOutSubscriber.subscribe((res) => {
      if (res === true) {
        this.signOut();
      }
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getConsoleRightsData() {
    const subs = this.consoleRightsService.get().subscribe(
      (data) => {
        this.rootScopeData.consoleRights = new Map(
          data.consoleTypeRights.map((i: { consoleType: string }) => [i.consoleType, i] as [string, any])
        );

        // Needs to add all the condition to show door banner
        //this.rootScopeData.showDoorBanner = this.rootScopeData.deviceType != 'phone';
      },
      (error) => {
        console.error('API failed.');
      }
    );
    this.sub.add(subs);
  }

  private async getFacilities() {
    this.loader.startProcess();
    try {
      await this._facilityService.get();
      await this.featureService.initialize();
      this.fcHybridLegacHalo = this.featureService.isOn('facility-connect-hybrid-mode');
      this.checkedFCHybridMode = true;
      // const defaultRoute = this.fcHybridLegacHalo
      //   ? 'equipmentSettings/announcement'
      //   : this.rootScopeData.currentUrl ?? '/dashboard';
      this.router.navigate([this.rootScopeData.currentUrl ?? 'equipmentSettings/announcement']);
    } catch (error) {
      if (error != 401) this.router.navigateByUrl(`/errors?error=${error}`);
    }
    this.loader.endProcess();
  }

  closeUnSavedChangesModal() {
    this.rootScopeData.showUnSavedChangesModal = false;
    this.rootScopeData.isFromFacilitySelection = '';
    this.rootScopeData.pathToNavigate = this.router.url;
  }

  moveAwayFromCurrentPage() {
    if (Array.isArray(this.rootScopeData.pathToNavigate)) {
      this.router.navigate(this.rootScopeData.pathToNavigate);
    } else if (this.rootScopeData.pathToNavigate === 'signout') {
      this.signOut();
    } else if (this.rootScopeData.pathToNavigate === 'purchasePlan') {
      this.goToPurchasePage();
    } else if (this.checkIsCustomizeBrandingPages()) {
      this.router.navigateByUrl(`videoLibrary/customizeBranding/${this.rootScopeData.pathToNavigate}`);
    } else if (this.rootScopeData.pathToNavigate.indexOf('app.html') >= 0) {
      const path = this.rootScopeData.pathToNavigate;
      window.location.href = path.replace('^', '');
    } else if (this.rootScopeData.isFromFacilitySelection) {
      this._facilityService.selectFacility(this.rootScopeData.isFromFacilitySelection, true);
      this.rootScopeData.isFromFacilitySelection = '';
    } else if (this.rootScopeData.pathToNavigate !== this.router.url) {
      if (this.rootScopeData.pathToNavigateQueryParam) {
        this.router.navigateByUrl(this.rootScopeData.pathToNavigate, this.rootScopeData.pathToNavigateQueryParam);
        this.rootScopeData.pathToNavigateQueryParam = null;
      } else {
        this.router.navigate([this.rootScopeData.pathToNavigate]);
      }
    }
    this.rootScopeData.isPageHasUnsavedChanges = false;
    this.closeUnSavedChangesModal();
  }

  checkIsCustomizeBrandingPages(): boolean {
    return (
      this.rootScopeData.pathToNavigate === 'addLogo' ||
      this.rootScopeData.pathToNavigate === 'banner' ||
      this.rootScopeData.pathToNavigate === 'categoryDisplayName'
    );
  }

  signOut(): void {
    let vtToken;
    this.isSSOEnabled = false;
    if (sessionStorage.getItem('vtTokenKey')) {
      vtToken = sessionStorage.getItem('vtTokenKey');
    } else {
      vtToken = localStorage.getItem('vtTokenKey');
    }

    if (vtToken) {
      this.checkSsoEnabledService.checkSSOEnabled(vtToken).subscribe(
        (data) => {
          if (data.source === 'SSO') {
            this.isSSOEnabled = true;
          }
          this.getUserLogout();
        },
        (error) => {
          this.getUserLogout();
        }
      );
    }
  }

  getUserLogout(): void {
    this.indexPath = '/halo.html#/login';
    this.indexParams = '';
    if (localStorage.getItem('selectedLang')) {
      this.indexParams = encodeURIComponent('?lang=' + localStorage.getItem('selectedLang'));
    }
    this.ssoRedirectionParams = encodeURIComponent(
      '?end_url=' + environment.currentHostPath + this.indexPath + '?lang=' + localStorage.getItem('selectedLang')
    );

    //$('#atlwdg-trigger').css('display', 'none');
    this.userLogoutService.userLogout().subscribe(
      (data) => {
        this.logoutData = data;
        if (this.isSSOEnabled) {
          this.afterLoutout();
          // need to add auto logout scenario when we do sso auto logout after linking
          window.location.href =
            environment.oamHostPath + '/oam/server/logout' + decodeURIComponent(this.ssoRedirectionParams);
        } else {
          this.afterLoutout();
          // need to add auto logout scenario when we do sso auto logout after linking
          this.authenticationService.signoutReset();
          this.multiFacService.resetMultiFacilitiesList();
          this._facilityService.reset();
          this.router.navigate(['/login']);
        }
      },
      (error) => {
        this.afterLoutout();
        this.authenticationService.signoutReset();
        this.multiFacService.resetMultiFacilitiesList();
        this._facilityService.reset();
        this.router.navigate(['/login']);
      }
    );
  }

  afterLoutout() {
    this.clearStorageAndCookies();
    this.rootScopeData.isAuthenticated = false;
  }

  clearStorageAndCookies(): void {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('vtTokenKey');
    localStorage.removeItem('vtUserType');
    localStorage.removeItem('vtManagerType');
    localStorage.removeItem('ssoFederated');
    localStorage.removeItem('newEquipmentAdded');
    localStorage.removeItem('isSsoFederated');
    localStorage.removeItem('lefNavigationExpandStatus');

    sessionStorage.removeItem('vtTokenKey');
    sessionStorage.removeItem('hideSurveyPopup');
    sessionStorage.removeItem('hideOutdatedOnboardingPopup');

    this.cookieService.delete('vtTokenKey');
    this.cookieService.delete('ssoCookie');
    this.cookieService.delete('ssoFederated');
    this.cookieService.delete('ssolang');
    this.cookieService.delete('vtManagerType');
    this.cookieService.delete('vtUserType');
    this.cookieService.delete('isSsoFederated');
    this.cookieService.delete('vtImpersonatorTokenKey');

    setTimeout(() => {
      this._facilityService.reset();
    }, 3000);
  }

  closeToast(): void {
    this.showToast = false;
  }

  enableToast(msg: any): void {
    this.showToast = true;
    this.toastMessage = msg;
  }

  closeAnnouncementModal(modal: string): void {
    this.showAnnouncementModal = false;
    if (this.rootScopeData.announcementModals.length > 0) {
      if (modal === this.rootScopeData.announcementModals[this.rootScopeData.announcementModals.length - 1])
        this.rootScopeData.announcementModals.pop();
      else {
        const index = this.rootScopeData.announcementModals.indexOf(modal);
        this.rootScopeData.announcementModals.splice(index, 1);
      }
    }

    this.rootScopeService.updateRootScope(this.rootScopeData);
  }

  enableAnnouncementModal() {
    setTimeout(() => {
      this.showAnnouncementModal = true;
    }, 500);
  }

  goToPurchasePage() {
    this._subscriptionService.getToken();
  }

  onTriggerBackRoute(path: string) {
    if (this.rootScopeData.isPageHasUnsavedChanges) {
      this.rootScopeData.pathToNavigate = path;
      this.rootScopeData.showUnSavedChangesModal = true;
    } else {
      if (this.rootScopeData.previousUrl === '/service/maintenance') {
        path = '/service/maintenance';
      }
      this.router.navigateByUrl(path);
    }
  }
}